import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Fade,
  Card,
  CardContent,
  Container,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { format } from 'date-fns';
import { FaChartLine, FaUsers, FaUser, FaSignOutAlt, FaBars } from 'react-icons/fa';
import LanguageSwitcher from '../components/LanguageSwitcher';
import i18n from '../i18n';

const ENCRYPTION_KEY = 'dadash123@@123##';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const encryptedData = localStorage.getItem('user');
    if (encryptedData) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        setUser(JSON.parse(decrypted));
      } catch (err) {
        localStorage.removeItem('user');
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return '';
    }
  };

  const menuItems = [
    { text: t('homePage.subscribers'), icon: <FaUsers />, path: '/subscribers' },
    { text: t('homePage.signals'), icon: <FaChartLine />, path: '/signals' },
    { text: t('homePage.profile'), icon: <FaUser />, path: '/profile' },
  ];

  const drawer = (
    <Box sx={{ p: 2, bgcolor: '#fff' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(0,255,0,0.1)',
                boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: '#1e3c72' }}>{item.icon}</Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
        <ListItem
          onClick={handleLogout}
          sx={{
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(255,0,0,0.1)',
              boxShadow: '0 0 8px rgba(255,0,0,0.3)',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: '#1e3c72' }}><FaSignOutAlt /></Box>
            <ListItemText primary={t('homePage.logout')} />
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 0.3; }
            100% { transform: scale(1); opacity: 0.5; }
          }
          @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideInRight {
            0% { transform: translateX(100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes vibrate {
            0% { transform: translateX(0); }
            20% { transform: translateX(-2px); }
            40% { transform: translateX(2px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
            100% { transform: translateX(0); }
          }
          @keyframes typing {
            0% { width: 0; }
            100% { width: 100%; }
          }
          @keyframes glow {
            0% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { text-shadow: 0 0 15px rgba(0, 255, 0, 0.7), 0 0 15px rgba(255, 0, 0, 0.7); }
            100% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
          }
        `}
      </style>
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 255, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 0, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out reverse',
        }}
      />
      <AppBar
        position="static"
        key={`appbar-${i18n.language}`}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            key={`title-${i18n.language}`}
            sx={{
              color: '#1e3c72',
              fontWeight: 'bold',
              textShadow: '0 0 8px rgba(0,255,0,0.2)',
              animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
            }}
          >
            {t('homePage.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: '#1e3c72',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      color: '#fff',
                      bgcolor: '#2a5298',
                      boxShadow: '0 0 10px rgba(0,255,0,0.4), 0 0 10px rgba(255,0,0,0.4)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                startIcon={<FaSignOutAlt />}
                onClick={handleLogout}
                sx={{
                  color: '#1e3c72',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: '#d32f2f',
                    boxShadow: '0 0 10px rgba(255,0,0,0.5)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {t('homePage.logout')}
              </Button>
            </Box>
            <LanguageSwitcher />
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: '#1e3c72' }}
            >
              <FaBars />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor={i18n.language === 'fa' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 240 } }}
      >
        {drawer}
      </Drawer>
      <Container sx={{ py: 4 }}>
        <Fade in timeout={600}>
          <Card
            className={i18n.language === 'fa' ? 'slide-in-right' : 'slide-in-left'}
            sx={{
              maxWidth: 600,
              mx: 'auto',
              borderRadius: 3,
              boxShadow: `
                0 8px 24px rgba(0,0,0,0.2),
                0 0 20px rgba(0,255,0,0.2),
                0 0 20px rgba(255,0,0,0.2)
              `,
              bgcolor: 'rgba(255,255,255,0.95)',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: `
                  0 8px 20px rgba(0,0,0,0.2),
                  0 0 20px rgba(0,255,0,0.4),
                  0 0 20px rgba(255,0,0,0.4)
                `,
                animation: 'vibrate 0.3s ease-in-out',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  color: '#1e3c72',
                  fontWeight: 'bold',
                  mb: 2,
                  textShadow: '0 0 10px rgba(0,255,0,0.3)',
                  animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
                }}
              >
                {t('homePage.welcome')}, {user.Name || ''} {user.Family || ''}!
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#2a5298', mb: 3 }}
              >
                {t('homePage.description')} {formatDate(user.EndDate)}
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default Home;